import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import Sidebar from "../components/Sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import axios from "axios";
import { toast } from "sonner";
import { 
  Bell, 
  AlertTriangle,
  CheckCircle,
  Info,
  Trash2,
  Check,
  Target,
  Award
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Alerts() {
  const { token } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAlerts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/alerts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des alertes");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchAlerts();
  }, [fetchAlerts]);

  const markAsRead = async (alertId) => {
    try {
      await axios.put(`${API}/alerts/${alertId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.map(a => a.id === alertId ? { ...a, is_read: true } : a));
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const deleteAlert = async (alertId) => {
    try {
      await axios.delete(`${API}/alerts/${alertId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAlerts(alerts.filter(a => a.id !== alertId));
      toast.success("Alerte supprimée");
    } catch (error) {
      toast.error("Erreur");
    }
  };

  const getAlertIcon = (type) => {
    switch (type) {
      case "objective_completed": return <CheckCircle className="w-5 h-5 text-chart-1" />;
      case "objective_at_risk": return <AlertTriangle className="w-5 h-5 text-chart-4" />;
      case "objective_behind": return <AlertTriangle className="w-5 h-5 text-destructive" />;
      case "badge_earned": return <Award className="w-5 h-5 text-chart-4" />;
      default: return <Info className="w-5 h-5 text-chart-5" />;
    }
  };

  const getSeverityBadge = (severity) => {
    const config = {
      info: { label: "Info", className: "bg-chart-5 text-white" },
      warning: { label: "Attention", className: "bg-chart-4 text-white" },
      critical: { label: "Critique", className: "bg-destructive text-white" }
    };
    const c = config[severity] || config.info;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const unreadCount = alerts.filter(a => !a.is_read).length;

  if (loading) {
    return (
      <div className="min-h-screen flex">
        <Sidebar />
        <main className="flex-1 ml-[260px] p-8 bg-background">
          <div className="flex items-center justify-center h-full">
            <div className="animate-pulse-soft text-primary text-lg">Chargement...</div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 ml-[260px] p-8 bg-background" data-testid="alerts-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Manrope' }}>
                Alertes
              </h1>
              {unreadCount > 0 && (
                <Badge className="bg-destructive text-white">{unreadCount} non lues</Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">
              Notifications sur vos objectifs RSE et badges
            </p>
          </div>
        </div>

        {/* Alerts List */}
        <div className="space-y-4">
          {alerts.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <Bell className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-foreground mb-2">Aucune alerte</h3>
                <p className="text-sm text-muted-foreground">
                  Vous recevrez des notifications lorsque vos objectifs RSE nécessiteront une attention
                </p>
              </CardContent>
            </Card>
          ) : (
            alerts.map((alert) => (
              <Card 
                key={alert.id} 
                className={`card-hover ${!alert.is_read ? 'border-l-4 border-l-primary' : ''}`}
                data-testid={`alert-${alert.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                        alert.severity === 'critical' ? 'bg-destructive/10' :
                        alert.severity === 'warning' ? 'bg-chart-4/10' :
                        'bg-chart-1/10'
                      }`}>
                        {getAlertIcon(alert.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-foreground">{alert.title}</h3>
                          {getSeverityBadge(alert.severity)}
                          {!alert.is_read && (
                            <Badge variant="outline" className="text-xs">Nouveau</Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">{alert.message}</p>
                        <span className="text-xs text-muted-foreground">
                          {new Date(alert.created_at).toLocaleDateString('fr-FR', {
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {!alert.is_read && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => markAsRead(alert.id)}
                          title="Marquer comme lu"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-muted-foreground hover:text-destructive"
                        onClick={() => deleteAlert(alert.id)}
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>
    </div>
  );
}
